import { Box, Divider, Stack } from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

import { buildEonServiceAccountName } from './buildEonServiceAccountName';
import { getGcpSourceCode } from './gcpCode';
import { GcpSourceAdvanceOptions } from './gcpSourceAdvanceOptions';

import { StepErrorDialog } from '../../../../../components/dialogs/stepErrorDialog';
import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { DiscoveryConfigurationAccordion } from '../discoveryConfigurationAccordion';
import { extractErrorMessage } from '../errorHandling';

export interface GcpConnectSourceAccountState {
  connectMethod: 'terraform' | 'bash';
  onboardingFlow: boolean;
  projectId: string;
  enableGcsBucketNotificationManagement?: boolean;
}

const projectIdConfig = getCloudFieldConfig(Provider.Gcp, 'projectId');

export const ConnectGcpSourceAccount = (
  props: StepProps<GcpConnectSourceAccountState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [customName, setCustomName] = useState<string>('');
  const { gcpControlPlane } = useEnvironment();
  const user = useUser();

  const dal = useDAL();
  const { data: body } = dal.cloudAccounts.scanning.useList();
  const scanningProjectId = body?.accounts?.find(
    (account) => account.cloudProvider === Provider.Gcp
  )?.providerAccountId;

  const code = getGcpSourceCode(
    props.wizardState.projectId,
    user.eonAccount?.id ?? '',
    scanningProjectId ?? '',
    gcpControlPlane,
    props.wizardState.enableGcsBucketNotificationManagement ?? false,
    props.wizardState.connectMethod
  );

  const isMissingFields = !props.wizardState.projectId;

  const canGoNext = () => {
    const validationErrors = !projectIdConfig.validation.validateInput(
      props.wizardState.projectId
    );

    return !isDeploying && !validationErrors && !isMissingFields;
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.source.connect.gcp({
        name: customName || undefined,
        serviceAccountName: buildEonServiceAccountName(
          CloudAccountType.Source,
          props.wizardState.projectId,
          user.eonAccount?.id ?? '',
          scanningProjectId ?? ''
        ),
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `Tell us which project you want to connect, and then
                  ${
                    props.wizardState.connectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment.`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : 'Please fill valid project ID'
        }
        nextButtonText={
          props.wizardState.onboardingFlow ? 'Connect & Continue' : 'Connect'
        }
        backButtonText={'Cancel'}
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text='Before you start, make sure you can manage service accounts in your GCP environment.'
            connectMethod={props.wizardState.connectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({ ...props.wizardState, connectMethod });
            }}
          />
          <Stack gap='24px' divider={<Divider />}>
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title={bulletTitle}
                leftContent={
                  <Stack
                    gap='24px'
                    divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                  >
                    <Stack gap='24px'>
                      <FormTextField
                        title='Project ID'
                        placeholder={projectIdConfig.placeholder}
                        validation={projectIdConfig.validation}
                        value={props.wizardState.projectId || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            projectId: event.target.value,
                          })
                        }
                      />
                      <GcpSourceAdvanceOptions
                        enableGcsBucketNotificationManagement={
                          props.wizardState
                            .enableGcsBucketNotificationManagement
                        }
                        setEnableGcsBucketNotificationManagement={(
                          enableGcsBucketNotificationManagement
                        ) => {
                          props.setWizardState({
                            ...props.wizardState,
                            enableGcsBucketNotificationManagement,
                          });
                        }}
                      />
                    </Stack>
                    <CustomAccountDisplayName
                      entityName='project'
                      customName={customName}
                      setCustomName={setCustomName}
                    />
                  </Stack>
                }
                rightContent={
                  <CopyableCode
                    title={
                      props.wizardState.connectMethod === 'terraform'
                        ? 'main.tf'
                        : `gcp-source-setup.sh`
                    }
                    code={code}
                    blur={isMissingFields}
                  />
                }
              />
            </Box>
            {props.wizardState.onboardingFlow && (
              <Stack gap='24px'>
                <DiscoveryConfigurationAccordion />
                <Divider />
              </Stack>
            )}
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
